/**
 * Note-noise filters for the TicketDetailModal Notes tab.
 *
 * AutoTask + the email parser dump a lot of garbage into the notes stream:
 *   - Workflow-rule audit entries ("When a ticket is: ... Then execute...")
 *   - Tiny "from-line" stubs that are just `Name <email@addr>` (the email
 *     From header captured as its own note)
 *   - The full quoted reply chain on every email-sourced note
 *   - "**Created via email with message-id ...**" footer on email notes
 *
 * isSystemNoise(note) — true if the note should be hidden by default. User
 * can toggle "show system notes" to see them.
 *
 * cleanNoteBody(text) — strip the quoted history + the message-id footer.
 * Returns { clean, truncated } so the UI can show a "show full" toggle
 * when meaningful text was removed.
 *
 * Reported by jgoode 2026-06-17 against ticket T20260526.0019 — that thread
 * had 41 notes; after these filters it's ~10 useful ones.
 */

import type { TicketNote } from '../../services/types';

const EMAIL_LIKE = /^[\w\s.'-]+<[\w.+@-]+>\s*$/;

export function isSystemNoise(note: TicketNote): boolean {
  const desc = (note.description || '').trim();
  if (!desc) return true;

  // AutoTask workflow-rule audit entries
  if (desc.startsWith('When a ticket is:')) return true;
  if (desc.startsWith('The following workflow rule actions did not occur')) return true;

  // Tiny "from-line" stubs: <80 chars, matches "Name <email@addr>" pattern
  if (desc.length < 80 && EMAIL_LIKE.test(desc)) return true;

  return false;
}

// Reply-boundary patterns, ordered roughly by reliability. The cleaner walks
// all of them and truncates at the EARLIEST match — quoted history below
// the boundary is dropped.
const REPLY_BOUNDARIES: RegExp[] = [
  // Long horizontal-line separator Outlook uses
  /\n_{16,}\s*\n/,
  // Outlook-style reply headers (From: ... Sent: ... To: ...)
  /\nFrom\s*:\s*[^\n]+\nSent\s*:/i,
  /\nFrom\s*:\s*[^\n]+\nDate\s*:/i,
  // "On Mon, Jun 17, 2026 at 2:30 PM, John Doe wrote:" (Gmail / mobile)
  /\n\s*On\s+\w[^\n]{4,80}wrote\s*:/,
  // Outlook web/AT inline replies: "From <Name> <email@addr>:"
  /\n\s*From\s+[^<\n]+<[^>]+>:/,
  // AT-side ticket-merge artifact
  /\n-{2,}\s*Original\s+Description\s*-{2,}/i,
  // Outlook desktop divider
  /\n-{4,}\s*Original\s+Message\s*-{4,}/i,
  // Mobile signature lines that ALWAYS sit just above quoted history
  /\nGet\s+Outlook\s+for/i,
  /\nSent\s+from\s+my\s+(Verizon|iPhone|Android|Samsung)/i,
];

// "**Created via email with message-id ABC@example.com in reply to email
// with message-id DEF@example.com**" — strip from the trailing end.
const EMAIL_FOOTER = /\n\s*\*\*Created via email with message-id[\s\S]*$/m;

export function cleanNoteBody(text: string | null | undefined): { clean: string; truncated: boolean } {
  if (!text) return { clean: '', truncated: false };
  let s = text;

  // Strip the trailing message-id footer first (it can otherwise prevent the
  // body from collapsing to its meaningful payload).
  s = s.replace(EMAIL_FOOTER, '');

  // Find the earliest reply boundary
  let earliest = -1;
  for (const re of REPLY_BOUNDARIES) {
    const idx = s.search(re);
    if (idx >= 0 && (earliest < 0 || idx < earliest)) earliest = idx;
  }

  let truncated = false;
  if (earliest >= 0) {
    s = s.slice(0, earliest);
    truncated = true;
  }

  return { clean: s.trim(), truncated };
}
